import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card } from 'primereact/card';
import { Calendar } from 'primereact/calendar';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { getTruckStatus, updateTruckStatus, TruckHandlingUnit } from '../api';
import { formatDateTime } from '../utils';
import HandlingUnitDetailsDialog from './HandlingUnitDetailsDialog';
import PurchaseOrderDetailsDialog from './PurchaseOrderDetailsDialog';
import * as XLSX from 'xlsx';

const TruckStatus: React.FC = () => {
  const [date, setDate] = useState<Date>(() => {
    const savedDate = localStorage.getItem('truckStatusDate');
    return savedDate ? new Date(savedDate) : new Date();
  });
  const [loading, setLoading] = useState(true);
  const [truckData, setTruckData] = useState<Record<number, TruckHandlingUnit[]>>({});
  const [selectedHandlingUnit, setSelectedHandlingUnit] = useState<TruckHandlingUnit | null>(null);
  const [selectedPoNumber, setSelectedPoNumber] = useState<string | null>(null);
  const [huDialogVisible, setHuDialogVisible] = useState(false);
  const [poDialogVisible, setPoDialogVisible] = useState(false);

  const fetchTruckStatus = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getTruckStatus(date.toISOString());
      setTruckData(data);
    } catch (error) {
      console.error('Error fetching truck status:', error);
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    fetchTruckStatus();
  }, [fetchTruckStatus]);

  const handleHUClick = (hu: TruckHandlingUnit) => {
    setSelectedHandlingUnit(hu);
    setHuDialogVisible(true);
  };

  const handlePOClick = (poNumber: string) => {
    setSelectedPoNumber(poNumber);
    setPoDialogVisible(true);
  };

  const exportToExcel = () => {
    // Prepare data for export
    const exportData = Object.entries(truckData).flatMap(([truckNumber, handlingUnits]) =>
      handlingUnits.map(hu => ({
        'Truck Number': truckNumber,
        'HU Number': hu.huNumber,
        'PO Number': hu.poNumber,
        'Item Number': hu.itemNumber,
        'Product Name': hu.productName,
        'Ship To Party': hu.shipToParty,
        'Received Count': hu.receivedCount ?? '0',
        'Assigned Date': formatDateTime(new Date(hu.huTruckDateTime!))
      }))
    );

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Truck Status');

    // Generate filename with current date
    const fileName = `truck_status_${date.toISOString().split('T')[0]}.xlsx`;

    // Save file
    XLSX.writeFile(wb, fileName);
  };

  const huNumberTemplate = (hu: TruckHandlingUnit) => (
    <button
      onClick={() => handleHUClick(hu)}
      className="p-link text-blue-600 hover:text-blue-800 font-medium"
      style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
    >
      {hu.huNumber}
    </button>
  );

  const poNumberTemplate = (hu: TruckHandlingUnit) => (
    <button
      onClick={() => handlePOClick(hu.poNumber!)}
      className="p-link text-blue-600 hover:text-blue-800 font-medium"
      style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
    >
      {hu.poNumber}
    </button>
  );

  const { user } = useAuth();
  const isCustomer = user?.role === 'customer';

  const renderTruckTable = (truckNumber: number, handlingUnits: TruckHandlingUnit[]) => (
    <Card key={truckNumber} className="mb-3">
      <div className="flex justify-content-between align-items-center mb-3">
        <h3 className="text-xl font-medium m-0">Truck {truckNumber}</h3>
        <div className="flex align-items-center gap-2">
          <span className={`px-3 py-1 border-round text-sm font-medium ${
            handlingUnits[0]?.truckStatus === 'FULL' 
              ? 'bg-red-100 text-red-700' 
              : 'bg-green-100 text-green-700'
          }`}>
            {handlingUnits[0]?.truckStatus || 'OPEN'}
          </span>
          {!isCustomer && (
            handlingUnits[0]?.truckStatus === 'FULL' ? (
              <Button
                label="Mark Open"
                severity="success"
                size="small"
                onClick={async () => {
                  try {
                    await updateTruckStatus(truckNumber, date.toISOString().split('T')[0], 'OPEN', user?.username || '');
                    fetchTruckStatus();
                  } catch (error) {
                    console.error('Error updating truck status:', error);
                  }
                }}
              />
            ) : (
              <Button
                label="Mark Full"
                severity="danger"
                size="small"
                onClick={async () => {
                  try {
                    await updateTruckStatus(truckNumber, date.toISOString().split('T')[0], 'FULL', user?.username || '');
                    fetchTruckStatus();
                  } catch (error) {
                    console.error('Error updating truck status:', error);
                  }
                }}
              />
            )
          )}
        </div>
      </div>
      <DataTable value={handlingUnits} className="mb-3" stripedRows>
        <Column 
          field="huNumber" 
          header="HU Number" 
          body={huNumberTemplate}
          style={{ width: '150px' }}
        />
        <Column 
          field="poNumber" 
          header="PO Number"
          body={poNumberTemplate}
          style={{ width: '150px' }}
        />
        <Column 
          field="itemNumber" 
          header="Item Number"
          style={{ width: '150px' }}
        />
        <Column 
          field="productName" 
          header="Product Name"
          style={{ width: '200px' }}
        />
        <Column 
          field="shipToParty" 
          header="Ship To Party"
          style={{ width: '150px' }}
        />
        <Column 
          field="receivedCount" 
          header="Received Count"
          style={{ width: '150px' }}
          body={(hu: TruckHandlingUnit) => hu.receivedCount ?? '0'}
        />
        <Column 
          field="huTruckDateTime" 
          header="Assigned Date" 
          body={(hu: TruckHandlingUnit) => formatDateTime(new Date(hu.huTruckDateTime!))}
          style={{ width: '180px' }}
        />
      </DataTable>
    </Card>
  );

  return (
    <div className="p-4">
      <div className="flex align-items-center justify-content-between mb-4">
        <h2 className="text-2xl font-bold m-0">Truck Status - {date.toLocaleDateString()}</h2>
        <div className="flex align-items-center gap-2">
          <Button
            icon="pi pi-file-excel"
            className="p-button-success"
            onClick={exportToExcel}
            disabled={loading || Object.keys(truckData).length === 0}
            tooltip="Export to Excel"
          />
          <Calendar
            value={date}
            onChange={(e) => {
              const newDate = e.value as Date;
              setDate(newDate);
              localStorage.setItem('truckStatusDate', newDate.toISOString());
            }}
            showIcon
            dateFormat="yy-mm-dd"
            className="w-auto"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-content-center">
          <i className="pi pi-spin pi-spinner text-2xl"></i>
        </div>
      ) : (
        <div>
          {Object.entries(truckData).map(([truckNumber, handlingUnits]) => 
            renderTruckTable(parseInt(truckNumber), handlingUnits)
          )}
          {Object.keys(truckData).length === 0 && (
            <div className="text-center p-4 surface-100 border-round">
              No handling units assigned to trucks on this date.
            </div>
          )}
        </div>
      )}

      <HandlingUnitDetailsDialog
        visible={huDialogVisible}
        onHide={() => setHuDialogVisible(false)}
        handlingUnit={selectedHandlingUnit}
      />

      {selectedPoNumber && (
        <PurchaseOrderDetailsDialog
          visible={poDialogVisible}
          onHide={() => setPoDialogVisible(false)}
          poNumber={selectedPoNumber}
        />
      )}
    </div>
  );
};

export default TruckStatus;
